import { BACKEND_URI } from "@/constants/config"

import { fetchWithAuthRetry, getHeaders } from "./api"
import { fetchPrismAppRequest } from "./prism-app-request"

export interface MemoryItem {
  id: string
  user_id: string
  text: string
  type?: string // 'general', 'preference', 'project', etc. for LTM
  created_at: string
  last_updated_at?: string
  status?: string // 'active', 'discarded' for STM
  deleted_by_user: boolean
}

export interface MemoryResponse {
  stm: MemoryItem[]
  ltm: MemoryItem[]
  discarded_candidate_texts?: string[]
}

export async function getMemoriesApi(idToken?: string): Promise<MemoryResponse> {
  const response = await fetchWithAuthRetry(
    `${BACKEND_URI}/memory/`,
    {
      method: "GET",
      headers: getHeaders(idToken),
    },
    idToken
  )
  return await response.json()
}

export async function addMemoryApi(
  memoryType: "stm" | "ltm",
  item: Partial<MemoryItem>,
  idToken?: string
): Promise<MemoryItem> {
  const response = await fetchWithAuthRetry(
    `${BACKEND_URI}/memory/`,
    {
      method: "POST",
      headers: getHeaders(idToken),
      body: JSON.stringify({
        memory_type: memoryType,
        item: item,
      }),
    },
    idToken
  )
  return await response.json()
}

export interface PrismNoteOutcome {
  result: "saved" | "not_applied"
  reason: string | null
  conversation_uid: string | null
  producer_event_id: string | null
}

export interface PrismEventTarget {
  conversation_uid: string
  producer_event_id: string
  data_seq: number
}

/** Frozen FILTH claims shape (contracts.md section 5.3). */
export interface PrismMemoryClaim {
  claim_uid: string
  kind:
    | "note"
    | "saved"
    | "preference"
    | "fact"
    | "decision"
    | "commitment"
    | "imported"
    | "about_me"
    | "profile_field"
  predicate: string
  text: string
  trust: string
  origin: "user_assertion" | "legacy_unknown"
  provenance_quality: string
  confidence: number
  imported: boolean
  observed_at: number
  recorded_at: number
  validity: { state: "unknown" } | { state: "known"; from: number; to: number | null }
  conversation_uids: string[]
  group_claim_uids: string[]
  event_target: PrismEventTarget | null
  correction: {
    space_uid: string
    subject_entity_uid: string
    predicate: string
    claim_uid: string
  }
}

export interface PrismClaimsPage {
  schema_version: 1
  space_uid: string
  claims: PrismMemoryClaim[]
  next_cursor: string | null
}

export async function getPrismClaimsApi(
  idToken?: string,
  cursor?: string | null
): Promise<PrismClaimsPage> {
  const query = new URLSearchParams({ limit: "50" })
  if (cursor) query.set("cursor", cursor)
  const body = (await prismRequest(`/claims?${query}`, "GET", { idToken })) as {
    schema_version?: unknown
    claims?: unknown
  } | null
  if (body?.schema_version !== 1 || !Array.isArray(body.claims)) {
    throw new PrismMemoryError("unavailable", 503)
  }
  return body as PrismClaimsPage
}

export async function deleteAllImportedPrismMemoryApi(
  idToken?: string
): Promise<{ state: PrismForgetState }> {
  return {
    state: forgetStatus(await prismRequest("/claims/delete-all-imported", "POST", { idToken }))
      .state,
  }
}

export async function getPrismPauseApi(idToken?: string): Promise<{ paused: boolean }> {
  const body = (await prismRequest("/pause", "GET", { idToken })) as { paused?: unknown } | null
  if (typeof body?.paused !== "boolean") throw new PrismMemoryError("unavailable", 503)
  return { paused: body.paused }
}

export async function setPrismPauseApi(
  paused: boolean,
  idToken?: string
): Promise<{ paused: boolean }> {
  const body = (await prismRequest("/pause", "POST", { idToken, payload: { paused } })) as {
    paused?: unknown
  } | null
  if (typeof body?.paused !== "boolean") throw new PrismMemoryError("unavailable", 503)
  return { paused: body.paused }
}

export async function addPrismNoteApi(
  text: string,
  sessionId: string,
  options: { idToken?: string; turnUid?: string } = {}
): Promise<PrismNoteOutcome> {
  const { idToken, turnUid } = options
  if (!sessionId) throw new Error("A chat is required to save a memory note")
  const response = await fetchPrismAppRequest(
    `${BACKEND_URI}/memory/`,
    {
      method: "POST",
      headers: getHeaders(idToken),
      body: JSON.stringify({
        session_id: sessionId,
        item: { text },
        ...(turnUid ? { turn_uid: turnUid } : {}),
      }),
    },
    idToken
  )
  return await response.json()
}

export async function retractPrismClaimApi(
  claimUids: string[],
  conversationUids: string[],
  idToken?: string
): Promise<{ state: PrismForgetState }> {
  return {
    state: forgetStatus(
      await prismRequest("/claims/retract", "POST", {
        idToken,
        payload: { claim_uids: claimUids, conversation_uids: conversationUids },
      })
    ).state,
  }
}

export async function forgetPrismEventApi(
  target: { conversation_uid: string; producer_event_id: string; data_seq: number },
  idToken?: string
): Promise<{ state: PrismForgetState }> {
  return {
    state: forgetStatus(
      await prismRequest("/claims/forget-event", "POST", { idToken, payload: target })
    ).state,
  }
}

export async function updateMemoryApi(options: {
  memoryId: string
  memoryType: "stm" | "ltm"
  updates: Partial<MemoryItem>
  idToken?: string
}): Promise<MemoryItem> {
  const { memoryId, memoryType, updates, idToken } = options
  const response = await fetchWithAuthRetry(
    `${BACKEND_URI}/memory/${memoryId}`,
    {
      method: "PUT",
      headers: getHeaders(idToken),
      body: JSON.stringify({
        memory_type: memoryType,
        updates: updates,
      }),
    },
    idToken
  )
  return await response.json()
}

export async function deleteMemoryApi(
  memoryId: string,
  memoryType: "stm" | "ltm",
  idToken?: string
): Promise<void> {
  await fetchWithAuthRetry(
    `${BACKEND_URI}/memory/${memoryId}?type=${memoryType}`,
    {
      method: "DELETE",
      headers: getHeaders(idToken),
    },
    idToken
  )
}

export async function triggerMaintenanceApi(idToken?: string): Promise<void> {
  await fetchWithAuthRetry(
    `${BACKEND_URI}/memory/maintenance`,
    {
      method: "POST",
      headers: getHeaders(idToken),
    },
    idToken
  )
}

export async function approveMemoryCandidateApi(
  memoryText: string,
  idToken?: string
): Promise<MemoryItem> {
  const response = await fetchWithAuthRetry(
    `${BACKEND_URI}/memory/approve-candidate`,
    {
      method: "POST",
      headers: getHeaders(idToken),
      body: JSON.stringify({ text: memoryText }),
    },
    idToken
  )
  return await response.json()
}

export async function dismissMemoryCandidateApi(
  memoryText: string,
  idToken?: string
): Promise<void> {
  await fetchWithAuthRetry(
    `${BACKEND_URI}/memory/dismiss-candidate`,
    {
      method: "POST",
      headers: getHeaders(idToken),
      body: JSON.stringify({ text: memoryText }),
    },
    idToken
  )
}

// Personal Prism conversations and their forget state. Distinct from the
// legacy STM/LTM store above: a forget here fences Hello AI's local transport
// copy and queues a signed control; only Prism's typed receipt confirms it.
export type PrismForgetState = "queued" | "pending_auth" | "blocked" | "confirmed" | "none"

export type PrismMemoryErrorCode =
  | "disabled"
  | "pending_auth"
  | "blocked_authority"
  | "not_found"
  | "invalid_request"
  | "unavailable"
  | "no_open_stream"
  // "Delete all imported" refused; nothing was deleted. Not self-service:
  // an administrator's restore follow-up (R-65 purge) clears it (spec O-21).
  | "restore_reconcile_pending"

export interface PrismConversation {
  conversation_uid: string
}

export interface PrismConversationPage {
  conversations: PrismConversation[]
  next_cursor: string | null
}

export interface PrismForgetStatus {
  conversation_uid: string
  state: PrismForgetState
}

export interface PrismForgetStatusPage {
  forgets: PrismForgetStatus[]
  has_more: boolean
}

export class PrismMemoryError extends Error {
  readonly code: PrismMemoryErrorCode
  readonly status: number

  constructor(code: PrismMemoryErrorCode, status: number) {
    super(`Prism memory request failed: ${code}`)
    this.name = "PrismMemoryError"
    this.code = code
    this.status = status
  }
}

const PRISM_ERROR_CODES: ReadonlySet<string> = new Set<PrismMemoryErrorCode>([
  "disabled",
  "pending_auth",
  "blocked_authority",
  "not_found",
  "invalid_request",
  "unavailable",
  "no_open_stream",
  "restore_reconcile_pending",
])
const PRISM_FORGET_STATES: ReadonlySet<string> = new Set<PrismForgetState>([
  "queued",
  "pending_auth",
  "blocked",
  "confirmed",
  "none",
])

function forgetStatus(value: unknown): PrismForgetStatus {
  const item = (value ?? {}) as { conversation_uid?: unknown; state?: unknown }
  const state =
    typeof item.state === "string" && PRISM_FORGET_STATES.has(item.state) ? item.state : "blocked"
  // An unexpected state is never shown as finished.
  return { conversation_uid: String(item.conversation_uid), state: state as PrismForgetState }
}

async function prismRequest(
  path: string,
  method: "GET" | "POST",
  options: { idToken?: string; payload?: unknown } = {}
) {
  const { idToken, payload } = options
  // Plain fetch on purpose: these routes answer 401 `pending_auth` when the
  // delegated Prism authority is missing. fetchWithAuthRetry would treat that
  // as an expired app session, replay it and then force terminal recovery.
  const response = await fetch(`${BACKEND_URI}/memory/prism${path}`, {
    method,
    headers: getHeaders(idToken),
    cache: "no-store",
    ...(payload === undefined ? {} : { body: JSON.stringify(payload) }),
  })
  const body: unknown = await response.json().catch(() => null)
  if (!response.ok) {
    const code = (body as { error?: unknown } | null)?.error
    throw new PrismMemoryError(
      typeof code === "string" && PRISM_ERROR_CODES.has(code)
        ? (code as PrismMemoryErrorCode)
        : "unavailable",
      response.status
    )
  }
  return body
}

export async function getPrismConversationTitleApi(
  conversationUid: string,
  idToken?: string
): Promise<string | null> {
  const body = await prismRequest(
    `/conversations/${encodeURIComponent(conversationUid)}/title`,
    "GET",
    { idToken }
  )
  const title = (body as { title?: unknown } | null)?.title
  return typeof title === "string" && title.trim() ? title : null
}

export async function getPrismConversationsApi(
  idToken?: string,
  cursor?: string | null
): Promise<PrismConversationPage> {
  const query = new URLSearchParams({ limit: "50" })
  if (cursor) query.set("cursor", cursor)
  const body = (await prismRequest(`/conversations?${query}`, "GET", { idToken })) as {
    conversations?: { conversation_uid?: unknown }[]
    next_cursor?: unknown
  }
  return {
    conversations: (body.conversations ?? []).map((item) => ({
      conversation_uid: String(item.conversation_uid),
    })),
    next_cursor: typeof body.next_cursor === "string" ? body.next_cursor : null,
  }
}

export async function getPrismForgetStatusApi(idToken?: string): Promise<PrismForgetStatusPage> {
  const body = (await prismRequest("/forgets?limit=100", "GET", { idToken })) as {
    forgets?: unknown[]
    has_more?: unknown
  }
  return { forgets: (body.forgets ?? []).map(forgetStatus), has_more: body.has_more === true }
}

export async function getPrismWipeStatusApi(idToken?: string): Promise<{ complete: boolean }> {
  const body = await prismRequest("/status", "GET", { idToken })
  const complete = (body as { complete?: unknown } | null)?.complete
  if (typeof complete !== "boolean") throw new PrismMemoryError("unavailable", 503)
  return { complete }
}

export async function requestPrismForgetApi(
  conversationUid: string,
  idToken?: string
): Promise<PrismForgetStatus> {
  return forgetStatus(
    await prismRequest(`/conversations/${encodeURIComponent(conversationUid)}/forget`, "POST", {
      idToken,
    })
  )
}
